import { Box, Button, IconButton, InputAdornment, InputLabel, Modal, TextField, Typography } from "@mui/material";
import { modalStyles, hmiModalStyles, useStyles } from "./CheckUserPasswordModal.styles";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { changeHMICodeFormValidationSchema } from "validations/schemas";
import { checkUserPassword } from "api/queries/usersAPI";
import { ICheckUserPasswordModal, ICheckUserPasswordModalValues } from "./CheckUserPasswordModalInterfaces";

const CheckUserPasswordModal = ({
    open,
    onClose,
    onConfirmPassword,
    title,
    question,
    questionConfirmedButtonText,
    variant = "default",
}: ICheckUserPasswordModal) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [questionConfirmed, setQuestionConfirmed] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const initFormValues: ICheckUserPasswordModalValues = {
        currentPassword: "",
    };

    const handleOnClose = () => {
        setQuestionConfirmed(false);
        setShowCurrentPassword(false);
        onClose();
    };

    const handleCheckPasswordSubmit = async (
        values: ICheckUserPasswordModalValues,
        formikHelpers: FormikHelpers<ICheckUserPasswordModalValues>,
    ) => {
        setIsSaving(true);
        try {
            await checkUserPassword({ password: values.currentPassword });
        } catch (error) {
            setIsSaving(false);
            formikHelpers.setErrors({
                currentPassword: t("check_user_password_modal.wrong_password"),
            });
            return;
        }
        try {
            await onConfirmPassword();
            setIsSaving(false);
            handleOnClose();
        } catch (error) {
            setIsSaving(false);
            formikHelpers.setErrors({
                currentPassword: t("check_user_password_modal.server_error"),
            });
        }
    };

    return (
        <Modal open={open} onClose={handleOnClose} disableEscapeKeyDown keepMounted={false}>
            <Box sx={variant === "hmi" ? hmiModalStyles : modalStyles}>
                {!questionConfirmed && (
                    <>
                        <div className={variant === "hmi" ? classes.hmiModalHeader : classes.modalHeader}>{title}</div>
                        <div className={variant === "hmi" ? classes.hmiModalBody : classes.modalBody}>
                            <Typography className={variant === "hmi" ? classes.hmiQuestion : classes.question}>
                                {question}
                            </Typography>
                        </div>
                        <div className={variant === "hmi" ? classes.hmiFooter : classes.footer}>
                            <Button
                                variant="outlined"
                                color="info"
                                className={variant === "hmi" ? classes.hmiSubmitButton : classes.submitButton}
                                onMouseUp={handleOnClose}
                            >
                                {t("check_user_password_modal.no")}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                className={variant === "hmi" ? classes.hmiSubmitButton : classes.submitButton}
                                disabled={isSaving}
                                onMouseUp={() => setQuestionConfirmed(true)}
                            >
                                {t("check_user_password_modal.yes")}
                            </Button>
                        </div>
                    </>
                )}

                {questionConfirmed && (
                    <Formik
                        initialValues={initFormValues}
                        validationSchema={changeHMICodeFormValidationSchema}
                        onSubmit={handleCheckPasswordSubmit}
                    >
                        {({ errors, touched, values, handleChange }) => {
                            return (
                                <Form>
                                    <div className={variant === "hmi" ? classes.hmiModalHeader : classes.modalHeader}>
                                        {title}
                                    </div>
                                    <div className={variant === "hmi" ? classes.hmiModalBody : classes.modalBody}>
                                        <Typography
                                            className={variant === "hmi" ? classes.hmiQuestion : classes.question}
                                        >
                                            {t("check_user_password_modal.please_enter_your_password_to_confirm")}
                                        </Typography>

                                        <div className={classes.formContainer}>
                                            <Box className={classes.boxContainer}>
                                                <InputLabel id="current-password-label">
                                                    <Typography variant="subtitle2" className={classes.formLabel}>
                                                        {t("check_user_password_modal.password")}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    aria-labelledby="current-password-label"
                                                    fullWidth
                                                    id="current-password-input"
                                                    className={classes.textField}
                                                    name="currentPassword"
                                                    type={showCurrentPassword ? "text" : "password"}
                                                    placeholder={t("check_user_password_modal.placeholder")}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    disabled={isSaving}
                                                                    onMouseUp={() =>
                                                                        setShowCurrentPassword(!showCurrentPassword)
                                                                    }
                                                                    onMouseDown={(event) => event.preventDefault()}
                                                                    edge="end"
                                                                >
                                                                    {showCurrentPassword ? (
                                                                        <VisibilityOff />
                                                                    ) : (
                                                                        <Visibility />
                                                                    )}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    variant="outlined"
                                                    disabled={isSaving}
                                                    autoFocus={true}
                                                    value={values.currentPassword}
                                                    onChange={handleChange}
                                                    error={touched.currentPassword && Boolean(errors.currentPassword)}
                                                    helperText={
                                                        touched.currentPassword &&
                                                        errors.currentPassword &&
                                                        t(errors.currentPassword)
                                                    }
                                                />
                                            </Box>
                                        </div>
                                    </div>
                                    <div className={variant === "hmi" ? classes.hmiFooter : classes.footer}>
                                        <Button
                                            variant="outlined"
                                            color="info"
                                            className={
                                                variant === "hmi" ? classes.hmiSubmitButton : classes.submitButton
                                            }
                                            onMouseUp={handleOnClose}
                                        >
                                            {t("check_user_password_modal.cancel")}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            className={
                                                variant === "hmi" ? classes.hmiSubmitButton : classes.submitButton
                                            }
                                            disabled={isSaving}
                                            type="submit"
                                        >
                                            {questionConfirmedButtonText}
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
            </Box>
        </Modal>
    );
};

export default CheckUserPasswordModal;
